import React, { useState, useEffect } from 'react';
import { BookOpen, Plus, Search, Filter, Clock, Trash2, Edit2, X, Save, Tag } from 'lucide-react';
import { topicTemplateService, TopicTemplate } from '../../services/topicTemplate.service.ts';
import PageHeader, { pageHeaderPrimaryButtonClass } from '../dashboard/components/PageHeader.tsx';

function AgendaPool() {
  const [templates, setTemplates] = useState([] as TopicTemplate[]);
  const [categories, setCategories] = useState([] as string[]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null as TopicTemplate | null);

  const [formData, setFormData] = useState({
    title: '',
    duration: 15,
    type: 'bilgilendirme',
    category: ''
  });

  useEffect(() => {
    loadData();
    // loadData her render'da yeniden tanımlandığı için deps'e eklenmez;
    // sadece kategori değişince tekrar çağrılması yeterli.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [templatesData, categoriesData] = await Promise.all([
        topicTemplateService.getTemplates(selectedCategory || undefined),
        topicTemplateService.getCategories()
      ]);
      setTemplates(templatesData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Veriler yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = templates.filter(t =>
    t.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddTemplate = async () => {
    if (!formData.title.trim()) return;

    try {
      await topicTemplateService.createTemplate({
        title: formData.title,
        duration: formData.duration,
        type: formData.type,
        category: formData.category || undefined
      });
      await loadData();
      setShowAddModal(false);
      resetForm();
    } catch (error) {
      console.error('Şablon eklenemedi:', error);
    }
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate || !formData.title.trim()) return;

    try {
      await topicTemplateService.updateTemplate(editingTemplate.id, {
        title: formData.title,
        duration: formData.duration,
        type: formData.type,
        category: formData.category || undefined
      });
      await loadData();
      setEditingTemplate(null);
      resetForm();
    } catch (error) {
      console.error('Şablon güncellenemedi:', error);
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    if (!window.confirm('Bu gündem konusunu silmek istediğinize emin misiniz?')) return;

    try {
      await topicTemplateService.deleteTemplate(id);
      setTemplates(prev => prev.filter(t => t.id !== id));
    } catch (error) {
      console.error('Şablon silinemedi:', error);
    }
  };

  const startEditing = (template: TopicTemplate) => {
    setEditingTemplate(template);
    setFormData({
      title: template.title,
      duration: template.duration,
      type: template.type,
      category: template.category || ''
    });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      duration: 15,
      type: 'bilgilendirme',
      category: ''
    });
  };

  const cancelEdit = () => {
    setEditingTemplate(null);
    setShowAddModal(false);
    resetForm();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={BookOpen}
        title="Gündem Havuzu"
        description="Sık kullandığınız toplantı konularını kaydedin ve yeniden kullanın."
        actions={
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className={pageHeaderPrimaryButtonClass}
            aria-label="Yeni konu ekle"
          >
            <Plus className="w-5 h-5" />
            <span>Yeni Konu Ekle</span>
          </button>
        }
      />

      {/* Filters */}
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-white/10 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Konu ara..."
              aria-label="Konu ara"
              className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
            />
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              aria-label="Kategori filtresi"
              title="Kategori seçin"
              className="px-4 py-2.5 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500 [&>option]:bg-slate-800 [&>option]:text-white"
            >
              <option value="">Tüm Kategoriler</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-white/10 p-4">
          <div className="text-2xl font-bold text-white">{templates.length}</div>
          <div className="text-gray-400 text-sm">Toplam Konu</div>
        </div>
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-white/10 p-4">
          <div className="text-2xl font-bold text-white">{categories.length}</div>
          <div className="text-gray-400 text-sm">Kategori</div>
        </div>
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-white/10 p-4">
          <div className="text-2xl font-bold text-white">
            {templates.filter(t => t.type === 'karar').length}
          </div>
          <div className="text-gray-400 text-sm">Karar Konusu</div>
        </div>
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-white/10 p-4">
          <div className="text-2xl font-bold text-white">
            {templates.reduce((sum, t) => sum + t.usageCount, 0)}
          </div>
          <div className="text-gray-400 text-sm">Toplam Kullanım</div>
        </div>
      </div>

      {/* Templates List */}
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-white/10">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Henüz Gündem Konusu Yok</h3>
            <p className="text-gray-400 mb-6">
              {searchQuery 
                ? 'Aramanızla eşleşen konu bulunamadı.' 
                : 'İlk gündem konunuzu ekleyerek başlayın.'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => setShowAddModal(true)}
                className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold transition transform hover:scale-105 inline-flex items-center gap-2"
                aria-label="İlk konuyu ekle"
              >
                <Plus className="w-5 h-5" />
                <span>İlk Konuyu Ekle</span>
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-white/10">
            {filteredTemplates.map((template) => (
              <div
                key={template.id}
                className="p-4 hover:bg-white/5 transition group"
              >
                {editingTemplate?.id === template.id ? (
                  <div className="space-y-4">
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                      placeholder="Konu başlığı"
                      aria-label="Konu başlığı"
                      autoFocus
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="relative">
                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <input
                          type="number"
                          min={1}
                          max={480}
                          step={1}
                          inputMode="numeric"
                          value={formData.duration}
                          onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 0 })}
                          onBlur={(e) => {
                            const n = parseInt(e.target.value, 10);
                            if (!Number.isFinite(n) || n < 1) {
                              setFormData({ ...formData, duration: 15 });
                            } else if (n > 480) {
                              setFormData({ ...formData, duration: 480 });
                            }
                          }}
                          aria-label="Süre (dakika)"
                          title="Süre (dakika)"
                          className="w-full pl-9 pr-12 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                          placeholder="15"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 pointer-events-none">
                          dk
                        </span>
                      </div>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        aria-label="Konu tipi seçin"
                        title="Konu tipi"
                        className="px-4 py-2 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500 [&>option]:bg-slate-800 [&>option]:text-white"
                      >
                        <option value="bilgilendirme">Bilgilendirme</option>
                        <option value="karar">Karar Alınacak</option>
                      </select>
                      <input
                        type="text"
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                        placeholder="Kategori"
                        aria-label="Kategori"
                        list="categories"
                      />
                      <datalist id="categories">
                        {categories.map(cat => (
                          <option key={cat} value={cat} />
                        ))}
                      </datalist>
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={cancelEdit}
                        className="px-4 py-2 bg-white/10 text-gray-300 rounded-lg hover:bg-white/20 transition"
                        aria-label="İptal"
                      >
                        İptal
                      </button>
                      <button
                        onClick={handleUpdateTemplate}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2"
                        aria-label="Kaydet"
                      >
                        <Save className="w-4 h-4" />
                        Kaydet
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-medium truncate">{template.title}</h4>
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
                        <span className="flex items-center gap-1 text-gray-400">
                          <Clock className="w-4 h-4" />
                          {template.duration} dk
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          template.type === 'karar'
                            ? 'bg-orange-500/20 text-orange-300'
                            : 'bg-blue-500/20 text-blue-300'
                        }`}>
                          {template.type === 'karar' ? 'Karar Alınacak' : 'Bilgilendirme'}
                        </span>
                        {template.category && (
                          <span className="flex items-center gap-1 text-purple-400">
                            <Tag className="w-3 h-3" />
                            {template.category}
                          </span>
                        )}
                        <span className="text-gray-500 text-xs">
                          {template.usageCount} kez kullanıldı
                        </span>
                      </div>
                    </div>
                    {/* Mobilde her zaman gorulebilir; md+ hover'da goster (dokunmatik destegi) */}
                    <div className="flex items-center gap-1 sm:gap-2 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition flex-shrink-0">
                      <button
                        onClick={() => startEditing(template)}
                        className="p-2 min-h-[40px] min-w-[40px] flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 active:scale-95 rounded-lg transition"
                        aria-label={`${template.title} konusunu düzenle`}
                        title="Düzenle"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template.id)}
                        className="p-2 min-h-[40px] min-w-[40px] flex items-center justify-center text-gray-400 hover:text-red-400 hover:bg-red-500/10 active:scale-95 rounded-lg transition"
                        aria-label={`${template.title} konusunu sil`}
                        title="Sil"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl border border-white/10 p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Yeni Gündem Konusu</h3>
              <button
                onClick={cancelEdit}
                className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition"
                aria-label="Kapat"
                title="Kapat"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="topic-title" className="block text-sm font-medium text-gray-300 mb-2">
                  Konu Başlığı *
                </label>
                <input
                  id="topic-title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                  placeholder="Örn: Haftalık ilerleme raporu"
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="topic-duration" className="block text-sm font-medium text-gray-300 mb-2">
                    Süre (dakika)
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      id="topic-duration"
                      type="number"
                      min={1}
                      max={480}
                      step={1}
                      inputMode="numeric"
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 0 })}
                      onBlur={(e) => {
                        const n = parseInt(e.target.value, 10);
                        if (!Number.isFinite(n) || n < 1) {
                          setFormData({ ...formData, duration: 15 });
                        } else if (n > 480) {
                          setFormData({ ...formData, duration: 480 });
                        }
                      }}
                      title="Süre (dakika)"
                      className="w-full pl-9 pr-12 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                      placeholder="15"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 pointer-events-none">
                      dk
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {[5, 10, 15, 30, 45, 60].map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => setFormData({ ...formData, duration: preset })}
                        className={`px-2 py-0.5 rounded text-xs transition ${
                          formData.duration === preset
                            ? 'bg-purple-600 text-white'
                            : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        {preset} dk
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label htmlFor="topic-type" className="block text-sm font-medium text-gray-300 mb-2">
                    Konu Tipi
                  </label>
                  <select
                    id="topic-type"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    title="Konu tipi seçin"
                    className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500 [&>option]:bg-slate-800 [&>option]:text-white"
                  >
                    <option value="bilgilendirme">Bilgilendirme</option>
                    <option value="karar">Karar Alınacak</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="topic-category" className="block text-sm font-medium text-gray-300 mb-2">
                  Kategori (opsiyonel)
                </label>
                <input
                  id="topic-category"
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                  placeholder="Örn: Haftalık, Proje, Satış"
                  list="categories-modal"
                />
                <datalist id="categories-modal">
                  {categories.map(cat => (
                    <option key={cat} value={cat} />
                  ))}
                </datalist>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={cancelEdit}
                className="flex-1 px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium transition"
                aria-label="İptal"
              >
                İptal
              </button>
              <button
                onClick={handleAddTemplate}
                disabled={!formData.title.trim()}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Ekle"
              >
                Ekle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AgendaPool;